import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";

import {
  getGetCaseSLAConfigurationsQueryKey,
  getGetCategoriesQueryKey,
  useConfigureCaseSLA,
  useSetDefaultCategory,
  useUpdateCategory,
} from "@vega-api/generated/case-admin-v1/case-admin-v1";
import { type CategoryResponse } from "@vega-api/generated/types/categoryResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { StatusTag } from "@vega-core/components/StatusTag/StatusTag";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { useMutationErrorToast } from "../../hooks/useMutationErrorToast";

import { CategoryDescriptionHelp } from "./CategoryDescriptionHelp";
import { CategoryMembersSection } from "./CategoryMembersSection";
import { getEditCategoryValidationSchema } from "./validationSchema";

type FormValues = {
  name: string;
  description: string;
  slaHours: string;
};

type EditCategoryModalProps = {
  category: CategoryResponse;
  slaHours?: number;
  onClose: () => void;
};

export const EditCategoryModal = ({
  category,
  slaHours,
  onClose,
}: EditCategoryModalProps) => {
  const showToast = useToast();
  const queryClient = useQueryClient();
  const errorToast = useMutationErrorToast();

  const { mutate: updateCategory, isPending: isUpdatingCategory } =
    useUpdateCategory({
      mutation: {
        onError: errorToast(m.cases_settings_error_updateCategory()),
      },
    });

  const { mutate: configureSla, isPending: isConfiguringSla } =
    useConfigureCaseSLA({
      mutation: { onError: errorToast(m.cases_settings_error_updateSla()) },
    });

  const { mutate: setDefaultCategory, isPending: isSettingDefault } =
    useSetDefaultCategory({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetCategoriesQueryKey(),
          });
          showToast({
            title: m.cases_settings_success_setDefaultCategory(),
            variant: "success",
          });
        },
        onError: errorToast(m.cases_settings_error_setDefaultCategory()),
      },
    });

  const isPending = isUpdatingCategory || isConfiguringSla;

  const initialValues: FormValues = {
    name: category.name,
    description: category.description ?? "",
    slaHours: slaHours === undefined ? "" : String(slaHours),
  };

  const handleSaveSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getGetCategoriesQueryKey() });
    queryClient.invalidateQueries({
      queryKey: getGetCaseSLAConfigurationsQueryKey(),
    });
    showToast({
      title: m.cases_settings_success_updateCategory(),
      variant: "success",
    });
    onClose();
  };

  const handleSubmit = (values: FormValues) => {
    const nextHours = Number(values.slaHours);
    const slaChanged = slaHours === undefined || nextHours !== slaHours;

    updateCategory(
      {
        categoryId: category.id,
        data: {
          name: values.name.trim(),
          description: values.description.trim(),
        },
      },
      {
        onSuccess: () => {
          if (slaChanged) {
            configureSla(
              { data: { categoryId: category.id, slaInHours: nextHours } },
              { onSuccess: handleSaveSuccess },
            );
          } else {
            handleSaveSuccess();
          }
        },
      },
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={getEditCategoryValidationSchema()}
    >
      {({ handleSubmit: submitForm }) => (
        <ActionaryModal
          ModalButtonsGroupProps={{
            mainAction: submitForm,
            mainActionText: m.cases_settings_saveCategory(),
            mainActionLabel: m.cases_settings_saveCategory(),
            disabled: isPending,
            isLoading: isPending,
            secondaryAction: onClose,
          }}
          dataTestId="edit-category-modal"
          open
          setOpen={(value) => {
            if (!value) {
              onClose();
            }
          }}
          title={m.cases_settings_edit_category()}
        >
          <Form className="space-y-4 w-full" data-testid="edit-category-form">
            <InputField
              label={m.cases_settings_category_name()}
              name="name"
              type="text"
              withBorder
            />

            <TextareaField
              isOptional
              label={m.cases_settings_category_description()}
              labelTag={<CategoryDescriptionHelp />}
              name="description"
            />

            <InputField
              label={m.cases_settings_category_sla()}
              name="slaHours"
              type="number"
              withBorder
            />

            <div
              className="flex items-center justify-between gap-3"
              data-testid="category-default-row"
            >
              <span className="body-base-medium">
                {m.cases_settings_category_defaultCategory()}
              </span>
              {category.isDefault ? (
                <StatusTag dataTestId="category-default-badge" status="OPEN">
                  {m.cases_settings_category_isDefault()}
                </StatusTag>
              ) : (
                <ThemedButton
                  dataTestId="category-make-default-button"
                  disabled={isSettingDefault}
                  isLoading={isSettingDefault}
                  onClick={() =>
                    setDefaultCategory({ categoryId: category.id })
                  }
                  variant="secondary"
                >
                  {m.cases_settings_category_makeDefault()}
                </ThemedButton>
              )}
            </div>

            <CategoryMembersSection category={category} />
          </Form>
        </ActionaryModal>
      )}
    </Formik>
  );
};
